/* Guided harness finder.
   The entry banner opens a three step modal and ends on a result screen that
   carries the theme's own add to cart path. Cart posting and the drawer are
   handed back to the Eurus Alpine stores so this never forks the cart stack. */

if (!window.Eurus) window.Eurus = { loadedScript: new Set() };
if (!window.Eurus.loadedScript) window.Eurus.loadedScript = new Set();

if (!window.Eurus.loadedScript.has('fuel01-harness-finder.js')) {
  window.Eurus.loadedScript.add('fuel01-harness-finder.js');

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';

  class Fuel01Finder extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      if (!this.config) return;

      this.modal = this.querySelector('[data-fuel01-modal]');
      this.card = this.querySelector('[data-fuel01-card]');
      this.steps = Array.from(this.querySelectorAll('[data-fuel01-step]'));
      this.opener = this.querySelector('[data-fuel01-open]');
      this.answers = { q1: null, q2: null, q3: null };
      this.current = '1';
      this.swatchCache = new Map();
      this.productCache = new Map();
      this.selection = [];
      this.product = null;
      this.branch = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.bind();
      this.setStep('1', { focus: false });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.unlock();
    }

    readConfig() {
      const node = this.querySelector('[data-fuel01-data]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('fuel01: config parse failed', error);
        return null;
      }
    }

    bind() {
      this.opener?.addEventListener('click', () => this.open());

      this.addEventListener('click', (event) => {
        const target = event.target;
        if (target.closest('[data-fuel01-close]')) {
          event.preventDefault();
          this.close();
        } else if (target.closest('[data-fuel01-next]')) {
          this.next();
        } else if (target.closest('[data-fuel01-prev]')) {
          this.prev();
        } else if (target.closest('[data-fuel01-atc]')) {
          this.addToCart();
        }
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-fuel01-answer]');
        if (!input) return;
        const key = input.dataset.fuel01Answer;
        this.answers[key] = input.value;
        for (const label of this.querySelectorAll(`[data-fuel01-step="${key.slice(1)}"] .fuel01-opt`)) {
          label.classList.toggle('is-selected', label.contains(input));
        }
        this.syncNext();
      });
    }

    /* ---------------------------------------------------------- modal */

    open() {
      this.modal.classList.add('is-open');
      this.lock();
      document.addEventListener('keydown', this.onKeydown);
      this.setStep('1');
    }

    close() {
      this.modal.classList.remove('is-open');
      this.unlock();
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
    }

    lock() {
      const popup = window.Alpine?.store?.('xPopup');
      if (popup) {
        popup.open = true;
      } else {
        document.body.style.overflow = 'hidden';
      }
    }

    unlock() {
      const popup = window.Alpine?.store?.('xPopup');
      if (popup) {
        popup.close();
      } else {
        document.body.style.overflow = '';
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(this.card.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ----------------------------------------------------------- steps */

    setStep(name, { focus = true } = {}) {
      this.current = name;
      for (const step of this.steps) {
        step.classList.toggle('is-active', step.dataset.fuel01Step === name);
      }
      this.card.classList.toggle('fuel01-modal__card--result', name === 'result');
      this.card.scrollTop = 0;
      this.syncNext();
      if (!focus) return;
      const active = this.steps.find((step) => step.dataset.fuel01Step === name);
      const target = active?.querySelector('.fuel01-q__heading, .fuel01-result__title');
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    /* The comp draws the Next pill filled on an unanswered step, so it keeps its
       fill and its place in the tab order and says it is unavailable through
       aria-disabled instead. */
    syncNext() {
      const step = this.steps.find((s) => s.dataset.fuel01Step === this.current);
      const button = step?.querySelector('[data-fuel01-next]');
      if (!button) return;
      button.setAttribute('aria-disabled', String(!this.answers[`q${this.current}`]));
    }

    next() {
      if (!this.answers[`q${this.current}`]) {
        const step = this.steps.find((s) => s.dataset.fuel01Step === this.current);
        step?.querySelector('[data-fuel01-answer]')?.focus();
        return;
      }
      if (this.current === '3') {
        this.showResult();
        return;
      }
      this.setStep(String(Number(this.current) + 1));
    }

    prev() {
      const target = this.current === 'result' ? '3' : String(Number(this.current) - 1);
      if (Number(target) < 1) return;
      this.setStep(target);
    }

    /* ---------------------------------------------------------- result */

    resolveBranch() {
      const { q1, q2, q3 } = this.answers;
      if (q1 === 'no') return 'sets';
      if (q2 === 'straps') return 'straps';
      if (q3 === 'boxer') return 'boxers';
      if (q3 === 'brief') return 'briefs';
      if (q3 === 'jock') return 'jocks';
      if (q3 === 'panty') return 'panty';
      return 'nopref';
    }

    showResult() {
      const key = this.resolveBranch();
      this.branch = this.config.branches[key] || null;
      this.setStep('result');

      const label = this.querySelector('[data-fuel01-result-label]');
      const body = this.querySelector('[data-fuel01-result-body]');
      const link = this.querySelector('[data-fuel01-collection-link]');

      if (label) label.textContent = this.branch?.label || '';
      if (body) body.textContent = this.branch?.body || '';
      if (link) {
        link.href = this.branch?.collectionUrl || '#';
        link.hidden = !this.branch?.collectionUrl;
      }

      if (!this.branch?.product) {
        this.renderMissingProduct();
        return;
      }

      this.setProduct(this.branch.product);
      this.loadSwatches(this.branch.product.swatchTag, this.branch.product.handle);
    }

    renderMissingProduct() {
      const image = this.querySelector('[data-fuel01-result-image]');
      const atc = this.querySelector('[data-fuel01-atc]');
      const options = this.querySelector('[data-fuel01-options]');
      const swatches = this.querySelector('[data-fuel01-swatches]');
      /* Drop the previous branch's product so nothing can be added from this
         screen; the collection link set by renderResult is the only way on. */
      this.product = null;
      this.selection = [];
      if (image) {
        image.removeAttribute('src');
        image.alt = '';
      }
      if (options) options.hidden = true;
      if (swatches) swatches.hidden = true;
      if (atc) {
        atc.hidden = true;
        atc.disabled = true;
      }
      this.setError('');
    }

    setProduct(product) {
      this.product = product;
      this.selection = (product.options || []).map((_, index) => {
        const firstAvailable = (product.variants || []).find((v) => v.available);
        const fallback = product.variants?.[0];
        return (firstAvailable || fallback)?.options?.[index] ?? null;
      });

      const image = this.querySelector('[data-fuel01-result-image]');
      if (image) {
        if (product.image) {
          image.src = product.image;
          image.alt = product.imageAlt || product.title || '';
        } else {
          image.removeAttribute('src');
          image.alt = '';
        }
      }

      this.renderOptions();
      this.setError('');
    }

    renderOptions() {
      const wrapper = this.querySelector('[data-fuel01-options]');
      if (!wrapper) return;
      wrapper.textContent = '';

      const product = this.product;
      const names = product?.options || [];
      const hide = !product || product.hasOnlyDefaultVariant || !names.length;
      wrapper.hidden = hide;
      if (hide) {
        this.syncAddToCart();
        return;
      }

      names.forEach((name, index) => {
        const values = [];
        for (const variant of product.variants) {
          const value = variant.options[index];
          if (value != null && !values.includes(value)) values.push(value);
        }

        const label = document.createElement('span');
        label.className = 'fuel01-sizes__label';
        label.textContent = `${name}:`;
        wrapper.appendChild(label);

        const row = document.createElement('div');
        row.className = 'fuel01-chips';
        row.setAttribute('role', 'group');
        row.setAttribute('aria-label', name);

        for (const value of values) {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = value.length > 3 ? 'fuel01-chip fuel01-chip--wide' : 'fuel01-chip';
          chip.textContent = value;
          chip.dataset.fuel01Option = String(index);
          chip.dataset.fuel01Value = value;

          const sellable = this.valueIsSellable(index, value);
          chip.disabled = !sellable;
          chip.setAttribute('aria-pressed', String(this.selection[index] === value));
          if (!sellable) {
            chip.setAttribute('aria-label', `${value} ${this.config.soldOutLabel}`);
          }
          chip.addEventListener('click', () => this.selectValue(index, value));
          row.appendChild(chip);
        }

        wrapper.appendChild(row);
      });

      this.syncAddToCart();
    }

    /* A value is sellable only against what the shopper has already chosen above
       it. Testing the index on its own offers sold out combinations as
       available on a two option product. Options below this one do not
       constrain it, so no pair of rows can lock each other out. */
    valueIsSellable(index, value) {
      return (this.product.variants || []).some((variant) => {
        if (variant.options[index] !== value || !variant.available) return false;
        for (let above = 0; above < index; above += 1) {
          const chosen = this.selection[above];
          if (chosen != null && variant.options[above] !== chosen) return false;
        }
        return true;
      });
    }

    selectValue(index, value) {
      const hadFocus = this.contains(document.activeElement);
      this.selection[index] = value;

      const variant = this.currentVariant();
      if (!variant || !variant.available) {
        // Changing one option can invalidate the ones under it. Re-derive those
        // and leave this click and everything above it alone.
        const rescue = (this.product.variants || []).find(
          (candidate) =>
            candidate.available &&
            this.selection.every(
              (chosen, other) => other > index || chosen == null || candidate.options[other] === chosen
            )
        );
        if (rescue) this.selection = [...rescue.options];
      }

      this.renderOptions();
      this.setError('');
      // renderOptions rebuilds the row, so the chip that was just activated is a
      // new element and focus would otherwise fall to <body>.
      if (hadFocus) this.focusChip(index, this.selection[index]);
    }

    focusChip(index, value) {
      for (const chip of this.querySelectorAll('[data-fuel01-value]')) {
        if (chip.dataset.fuel01Option === String(index) && chip.dataset.fuel01Value === value) {
          chip.focus();
          return;
        }
      }
    }

    currentVariant() {
      return (this.product?.variants || []).find((variant) =>
        this.selection.every((value, index) => value == null || variant.options[index] === value)
      );
    }

    syncAddToCart() {
      const atc = this.querySelector('[data-fuel01-atc]');
      const link = this.querySelector('[data-fuel01-collection-link]');
      if (!atc) return;
      atc.hidden = false;

      const variant = this.currentVariant();
      const sellable = Boolean(variant && variant.available);
      atc.disabled = !sellable;
      atc.textContent = sellable ? this.config.addToCartLabel : this.config.soldOutLabel;
      if (link) link.hidden = sellable || !this.branch?.collectionUrl;
    }

    setError(message) {
      const node = this.querySelector('[data-fuel01-error]');
      if (!node) return;
      node.textContent = message;
      node.hidden = !message;
    }

    /* ------------------------------------------------------- colourways */

    async loadSwatches(tag, activeHandle) {
      const strip = this.querySelector('[data-fuel01-swatches]');
      if (!strip) return;
      strip.textContent = '';
      strip.hidden = true;
      if (!tag) return;

      let group = this.swatchCache.get(tag);
      if (!group) {
        const query = encodeURIComponent(`tag:"${tag}"`);
        const url = `${this.config.searchUrl}?q=${query}&type=product&options%5Bprefix%5D=none&section_id=fuel01-finder-swatches`;
        try {
          const response = await fetch(url);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const data = parsed.querySelector('[data-fuel01-swatch-data]');
          group = data ? JSON.parse(data.textContent) : [];
          this.swatchCache.set(tag, group);
        } catch (error) {
          console.warn('fuel01: colourway lookup failed', error);
          return;
        }
      }

      if (group.length < 2) return;

      // The comp leads the strip with the colourway that is on screen, so the
      // active product comes first and the rest keep the store's own order.
      const ordered = [
        ...group.filter((item) => item.handle === activeHandle),
        ...group.filter((item) => item.handle !== activeHandle)
      ];

      for (const item of ordered) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fuel01-swatch';
        button.dataset.fuel01Handle = item.handle;
        button.setAttribute('aria-current', String(item.handle === activeHandle));

        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title;
        img.width = 78;
        img.height = 78;
        img.loading = 'lazy';
        button.appendChild(img);

        button.addEventListener('click', () => this.selectColourway(item.handle, strip));
        strip.appendChild(button);
      }
      strip.hidden = false;
    }

    async selectColourway(handle, strip) {
      if (this.product?.handle === handle) return;
      let product = this.productCache.get(handle);

      if (!product) {
        strip.classList.add('fuel01-is-busy');
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
          const data = await response.json();
          product = {
            handle: data.handle,
            title: data.title,
            url: data.url,
            image: data.featured_image || '',
            imageAlt: data.title,
            swatchTag: this.product?.swatchTag || '',
            hasOnlyDefaultVariant: data.variants.length === 1 && data.variants[0].title === 'Default Title',
            available: data.available,
            options: data.options.map((option) => (typeof option === 'string' ? option : option.name)),
            variants: data.variants.map((variant) => ({
              id: variant.id,
              available: variant.available,
              options: variant.options
            }))
          };
          this.productCache.set(handle, product);
        } catch (error) {
          console.warn('fuel01: colourway load failed', error);
          // A dropped request is not the same thing as a sold out product.
          this.setError(this.config.errorLabel || window.Eurus.cart_error || '');
          return;
        } finally {
          strip.classList.remove('fuel01-is-busy');
        }
      }

      this.setProduct(product);
      for (const button of strip.querySelectorAll('.fuel01-swatch')) {
        button.setAttribute('aria-current', String(button.dataset.fuel01Handle === handle));
      }
    }

    /* ------------------------------------------------------------- cart */

    async addToCart() {
      const variant = this.currentVariant();
      const atc = this.querySelector('[data-fuel01-atc]');
      if (!variant || !variant.available || !atc || atc.disabled) return;

      atc.disabled = true;
      this.setError('');

      const helper = window.Alpine?.store?.('xCartHelper');
      const body = new FormData();
      body.append('id', String(variant.id));
      body.append('quantity', '1');
      if (helper?.getSectionsToRender) {
        body.append('sections', helper.getSectionsToRender().map((section) => section.id).join(','));
        body.append('sections_url', window.location.pathname);
      }

      try {
        if (helper?.waitForCartUpdate) await helper.waitForCartUpdate();
        window.updatingCart = true;

        const response = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await response.json();

        if (String(data.status) === '422') {
          this.setError(data.description || data.message || '');
          return;
        }

        const noti = window.Alpine?.store?.('xCartNoti');
        if (noti?.enable) noti.setItem(data);
        if (helper?.reRenderSections) helper.reRenderSections(data.sections);
        const bubble = document.getElementById('cart-icon-bubble');
        if (helper && bubble) helper.currentItemCount = parseInt(bubble.innerHTML, 10);

        this.close();
        if (!noti?.enable) window.Alpine?.store?.('xMiniCart')?.openCart();

        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: data } }));
      } catch (error) {
        console.warn('fuel01: add to cart failed', error);
        this.setError(window.Eurus.cart_error || '');
      } finally {
        window.updatingCart = false;
        this.syncAddToCart();
      }
    }
  }

  if (!customElements.get('fuel01-finder')) {
    customElements.define('fuel01-finder', Fuel01Finder);
  }
}
